import React, { useState, useEffect } from 'react';
import { MobileNavigation, DesktopNavigation } from './Navigation';
import { AppBar, Toolbar, IconButton } from '@mui/material';
import { Menu as MenuIcon, Notifications as NotificationsIcon } from '@mui/icons-material';

interface LayoutProps {
  children: React.ReactNode;
}

const AppHeader: React.FC<{ onMenuClick: () => void }> = ({ onMenuClick }) => (
  <AppBar 
    position="fixed" 
    color="default" 
    elevation={0}
    className="border-b border-gray-200 dark:border-dark-border dark:bg-dark-paper z-20"
  >
    <Toolbar className="flex justify-between items-center px-4 h-16">
      <div className="flex items-center">
        <IconButton edge="start" color="inherit" aria-label="menu" onClick={onMenuClick} className="btn-icon">
          <MenuIcon />
        </IconButton>
        <h1 className="text-xl font-bold text-primary dark:text-white ml-3">Stockit</h1>
      </div>
      <IconButton color="inherit" aria-label="notifications" className="btn-icon">
        <NotificationsIcon />
      </IconButton>
    </Toolbar>
  </AppBar>
);

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile && isMobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  const handleMenuClick = () => {
    if (isMobile) {
      setMobileMenuOpen(!isMobileMenuOpen);
    } else {
      setIsSidebarOpen(!isSidebarOpen);
    }
  };

  const handleCloseMenu = () => {
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-light dark:bg-dark">
      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex md:flex-col md:fixed md:inset-y-0 md:z-20 ${isSidebarOpen ? 'md:w-64' : 'md:w-0'} transition-all duration-300 bg-white dark:bg-dark-paper border-r border-gray-200 dark:border-dark-border overflow-hidden`}>
        <div className="flex items-center h-16 px-6 border-b border-gray-200 dark:border-dark-border">
          <h1 className="text-xl font-bold text-primary dark:text-white">Stockit</h1>
        </div>
        <div className="flex-1 overflow-y-auto">
          <DesktopNavigation />
        </div>
      </aside>

      {/* Mobile Navigation Overlay */}
      {isMobileMenuOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
            onClick={handleCloseMenu}
          />
          <div className="fixed inset-y-0 left-0 w-64 z-40 md:hidden bg-white dark:bg-dark-paper">
            <MobileNavigation onLinkClick={handleCloseMenu} />
          </div>
        </>
      )}

      {/* Main Content */}
      <div className={`flex flex-col ${isSidebarOpen ? 'md:pl-64' : 'md:pl-0'} transition-all duration-300 min-h-screen`}>
        {/* Mobile Header */}
        <AppHeader onMenuClick={handleMenuClick} 
        />

        {/* Page Content */}
        <main className="flex-1 pt-16 md:pt-4">
          <div className="container mx-auto px-4 py-4 md:py-8 dark:text-dark-text-primary">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
